import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult, InteractionRequiredAuthError } from '@azure/msal-browser';
import { Numbers } from '@constants/numbers';
import { LoginFacadeService } from '@modules/autenticacion/login/store/login.facade.service';
import { EMPTY, Observable, from, of, throwError } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class CustomHttpInterceptorBearer implements HttpInterceptor {
  private readonly scopes = ['user.read'];

  constructor(
    private router: Router,
    public msalService: MsalService,
    private loginFacadeService: LoginFacadeService,
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    request = request.clone({
      setHeaders: { 'Content-Location': this.router.routerState.snapshot.url },
    });

    const token$: Observable<string | null> = request.url.endsWith('rentas/login')
      ? this.getMsalIdToken()
      : this.loginFacadeService.getToken().pipe(take(1));

    return token$.pipe(
      switchMap((token) => {
        if (token == null) {
          this.router.navigate(['']);
          return next.handle(request);
        }
        const authHeader = request.url.endsWith('rentas/login') ? `Bearer ${token}` : `${token}`;
        return next.handle(request.clone({ setHeaders: { Authorization: authHeader } }));
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === Numbers._401 && !request.url.includes('login')) {
          this.msalService.logout({ postLogoutRedirectUri: window.location.origin });
          return EMPTY;
        }
        return throwError(() => error);
      }),
    );
  }

  manageLoginExpired = (next: HttpHandler, request: HttpRequest<any>) =>
    this.loginFacadeService.refreshToken().pipe(switchMap(() => next.handle(request)));

  /** idToken vía API de MSAL (la caché en localStorage está cifrada en v4) */
  private getMsalIdToken(): Observable<string | null> {
    return this.acquireSilent().pipe(switchMap((r) => of(r?.idToken ?? null)));
  }

  /** accessToken vía API de MSAL (reemplaza extractMSALTokenAccess) */
  getMsalAccessToken(): Observable<string | null> {
    return this.acquireSilent().pipe(switchMap((r) => of(r?.accessToken ?? null)));
  }

  private acquireSilent(): Observable<AuthenticationResult | null> {
    const instance = this.msalService.instance;
    const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
    if (!account) {
      return of(null);
    }
    return from(instance.acquireTokenSilent({ scopes: this.scopes, account })).pipe(
      catchError((err) => {
        if (err instanceof InteractionRequiredAuthError) {
          instance.acquireTokenRedirect({ scopes: this.scopes, account });
        }
        return of(null);
      }),
    );
  }
}
